import { lessons } from '@/lib/marketing/content'

export function LessonsSpotlight() {
  return (
    <section className="py-16 sm:py-24">
      <div className="max-w-[960px] mx-auto px-4 sm:px-6">
        <div className="relative">
          {/* Background glow */}
          <div className="absolute -inset-6 bg-[#71ED7F]/[0.04] rounded-3xl blur-2xl pointer-events-none" />

          {/* Card */}
          <div className="relative bg-[#001716] rounded-2xl border border-[#71ED7F]/20 p-6 sm:p-10 overflow-hidden shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4)]">
            {/* Decorative gradient */}
            <div className="absolute top-0 left-0 w-80 h-80 bg-[#71ED7F]/[0.06] rounded-full blur-[100px] pointer-events-none" />

            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              {/* Left: Content */}
              <div>
                <div className="inline-block px-3 py-1 rounded-full bg-[#71ED7F]/20 text-[#71ED7F] text-xs font-semibold uppercase tracking-wider mb-4">
                  {lessons.badge}
                </div>

                <h2 className="text-2xl sm:text-3xl font-medium font-display text-white mb-4">
                  {lessons.headline}
                </h2>

                <p className="text-white/50 text-sm sm:text-base mb-6 leading-relaxed">
                  {lessons.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {lessons.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <svg className="w-4 h-4 text-[#71ED7F] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm text-white/60">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#demo"
                  className="inline-flex items-center gap-2 text-[#71ED7F] text-sm font-medium hover:gap-3 transition-all"
                >
                  {lessons.cta}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>

              {/* Right: Instructor Selection Mock */}
              <div className="relative">
                <div className="bg-[#002C2B] rounded-xl border border-white/10 p-4 shadow-2xl">
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/5">
                    <svg className="w-4 h-4 text-[#71ED7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                    </svg>
                    <span className="text-sm text-white/70">Select Instructor</span>
                  </div>

                  {/* Instructor cards */}
                  <div className="space-y-3">
                    {/* Selected instructor */}
                    <div className="flex items-center gap-3 p-3 bg-[#71ED7F]/[0.08] rounded-lg border border-[#71ED7F]/30">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#71ED7F]/30 to-[#71ED7F]/10 border border-[#71ED7F]/20 flex items-center justify-center">
                        <span className="text-[#71ED7F] font-semibold text-sm">JD</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-white">John Davis</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#71ED7F]/20 text-[#71ED7F]">PGA</span>
                        </div>
                        <div className="text-xs text-white/40">$75/hr • Next: Today 2pm</div>
                      </div>
                      <div className="w-5 h-5 rounded-full bg-[#71ED7F] flex items-center justify-center">
                        <svg className="w-3 h-3 text-[#001716]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>

                    {/* Other instructors */}
                    <div className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.06] hover:bg-white/[0.05] transition-colors">
                      <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white/50 font-semibold text-sm">SM</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white/70">Sarah Miller</div>
                        <div className="text-xs text-white/40">$60/hr • Next: Tomorrow 10am</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.06] hover:bg-white/[0.05] transition-colors">
                      <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                        <span className="text-white/50 font-semibold text-sm">RJ</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white/70">Robert Jones</div>
                        <div className="text-xs text-white/40">$85/hr • Next: Wed 4pm</div>
                      </div>
                    </div>
                  </div>

                  {/* Duration selector */}
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <div className="text-xs text-white/40 mb-2">Duration</div>
                    <div className="flex gap-2">
                      <button className="flex-1 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-white/60">
                        30 min
                      </button>
                      <button className="flex-1 py-2 rounded-lg bg-[#71ED7F]/10 border border-[#71ED7F]/30 text-xs text-[#71ED7F] font-medium">
                        60 min
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
